import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const UPDATED = "18 augusti 2026";

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-head font-semibold text-lg text-foreground">{title}</h2>
      <div className="text-sm text-muted-foreground mt-2 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Integritetspolicy() {
  return (
    <div className="hamn-bg min-h-screen">
      <header className="sticky top-0 z-30 border-b border-border bg-white/90 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="policy-home-link">
            <img src="/hamn-logo.png" alt="HAMN CV Studio" className="h-9 w-9 rounded-lg object-cover" />
            <div className="font-head font-bold text-primary">HAMN CV Studio</div>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="policy-back-link">
            <ArrowLeft className="h-4 w-4" /> Till startsidan
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 md:py-16" data-testid="integritetspolicy-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><ShieldCheck className="h-5 w-5" /></div>
          <h1 className="font-head font-bold text-3xl sm:text-4xl text-primary">Integritetspolicy</h1>
        </div>
        <p className="text-sm text-muted-foreground">Senast uppdaterad: {UPDATED}</p>

        <Section title="1. Inledning">
          <p>HAMN CV Studio ("HAMN", "vi", "oss") hjälper dig att samla din verkliga kompetens och skapa jobbanpassade CV:n med hjälp av AI. Din integritet är central för oss – tjänsten är byggd enligt <strong>privacy-by-design</strong>. Denna policy beskriver hur vi behandlar dina personuppgifter enligt EU:s dataskyddsförordning (GDPR).</p>
        </Section>

        <Section title="2. Personuppgiftsansvarig">
          <p>Personuppgiftsansvarig för behandlingen är HAMN CV Studio (hamninstitute.se). Har du frågor om hur vi hanterar dina uppgifter når du oss på <a href="mailto:ludwig_hamn@outlook.com" className="text-primary underline underline-offset-2">ludwig_hamn@outlook.com</a> eller via <Link to="/hjalp" className="text-primary underline underline-offset-2">Hjälp &amp; feedback</Link>.</p>
        </Section>

        <Section title="3. Vilka uppgifter vi behandlar">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Kontouppgifter:</strong> namn, e-postadress och krypterat lösenord.</li>
            <li><strong>Masterprofil:</strong> arbetslivserfarenhet, utbildning, kompetenser, system/verktyg, karaktär och styrkor, intressen samt framtidsriktning – uppgifter du själv väljer att lägga in.</li>
            <li><strong>CV-data:</strong> CV-versioner du skapar, jobbannonser du klistrar in samt matchningsanalyser.</li>
            <li><strong>Referenser:</strong> uppgifter om referenspersoner du bjuder in och deras svar samt eventuella bilagor (intyg).</li>
            <li><strong>Hjälp &amp; feedback:</strong> meddelanden du skickar och eventuell bifogad skärmdump.</li>
            <li><strong>Teknisk logg:</strong> säkerhetshändelser (t.ex. inloggningar) och IP-adress för att förhindra missbruk.</li>
          </ul>
        </Section>

        <Section title="4. Känsliga uppgifter och personnummer">
          <p>HAMN använder <strong>aldrig</strong> känsliga personuppgifter som kön, etnicitet, religion, hälsa, politiska åsikter eller sexuell läggning för CV-optimering, matchning eller AI-bearbetning. Lägg inte in sådana uppgifter i din profil.</p>
          <p>Vi rekommenderar dessutom starkt att du <strong>inte anger fullständigt personnummer</strong>, bankuppgifter eller ID-handlingsnummer i din profil, i ditt CV eller i uppladdade dokument. Ett CV bör bara innehålla namn, kontaktuppgifter och ort. Om du laddar upp betyg eller intyg – maska gärna bort personnummer och andra känsliga uppgifter först. Arbetsgivare behöver ditt personnummer först vid en eventuell anställning, aldrig i ansökan.</p>
        </Section>

        <Section title="5. Ändamål och rättslig grund">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Tillhandahålla tjänsten</strong> (skapa konto, spara profil, generera CV) – rättslig grund: fullgörande av avtal.</li>
            <li><strong>AI-genererade CV:n och vägledning</strong> – rättslig grund: avtal och ditt samtycke. AI får endast använda de uppgifter du lagt in och hittar aldrig på information.</li>
            <li><strong>Referensinbjudningar och e-post</strong> – rättslig grund: ditt samtycke.</li>
            <li><strong>Säkerhet och loggning</strong> – rättslig grund: berättigat intresse att skydda konton mot obehörig åtkomst.</li>
          </ul>
        </Section>

        <Section title="6. AI-behandling och transparens (EU:s AI-förordning)">
          <p>CV-generering, matchningsanalys och vägledning sker med en språkmodell (Claude via Emergent). Endast de uppgifter som behövs för den aktuella uppgiften skickas för bearbetning, och AI instrueras att aldrig fabricera erfarenhet, kompetens, utbildning, system, certifikat eller resultat.</p>
          <p>Vi arbetar i linje med <strong>EU:s AI-förordning (AI Act)</strong>, inklusive transparenskraven i artikel 50:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Tydlig märkning:</strong> Allt innehåll som skapas av AI (t.ex. CV-texter, matchningsanalyser och vägledning) märks tydligt som AI-genererat i tjänsten. Exporterade CV:n (PDF/Word) förses dessutom med maskinläsbar märkning i filens metadata om att innehållet är AI-genererat.</li>
            <li><strong>Du behåller kontrollen (mänsklig tillsyn):</strong> AI:n ger dig ett utkast och förslag – du granskar, redigerar och beslutar alltid själv. Inga beslut fattas automatiskt åt dig.</li>
            <li><strong>Inte ett gallringsverktyg:</strong> HAMN är ett verktyg för dig som arbetssökande. Vi använder inte AI för att automatiskt bedöma, rangordna eller välja bort kandidater åt arbetsgivare.</li>
            <li><strong>Inga känsliga uppgifter eller profilering:</strong> AI:n använder aldrig kön, etnicitet, religion, hälsa, politik eller läggning, och gör ingen dold profilering av dig.</li>
            <li><strong>Verklig data:</strong> Jobbannonser hämtas från Arbetsförmedlingens öppna Platsbanken och utbildningar från Skolverkets öppna data – AI:n tolkar och vägleder utifrån verkliga källor, aldrig påhittade.</li>
          </ul>
        </Section>

        <Section title="7. Mottagare och tredjepartsleverantörer">
          <p>Vi delar aldrig dina uppgifter för marknadsföring. För att leverera tjänsten anlitar vi personuppgiftsbiträden:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>AI-leverantör (Claude/Emergent):</strong> bearbetning av CV-text och analyser.</li>
            <li><strong>Resend:</strong> utskick av e-post (referensinbjudningar, lösenordsåterställning, feedback).</li>
            <li><strong>Objektlagring:</strong> säker lagring av uppladdade filer (intyg, skärmdumpar).</li>
          </ul>
        </Section>

        <Section title="8. Lagringstid">
          <p>Vi sparar dina uppgifter så länge du har ett konto. När du raderar ditt konto raderas din profil, dina CV:n och tillhörande data. Säkerhetsloggar och tokens rensas automatiskt (t.ex. återställningslänkar efter 30 minuter).</p>
        </Section>

        <Section title="9. Cookies och lokal lagring">
          <p>HAMN använder endast <strong>nödvändig lokal lagring</strong> (localStorage) för att hålla dig inloggad. Vi använder inga cookies för analys, spårning eller marknadsföring, och delar därför inga uppgifter med annonsnätverk.</p>
        </Section>

        <Section title="10. Dina rättigheter">
          <p>Du har rätt att:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>få tillgång till dina uppgifter (exportera som JSON i Inställningar),</li>
            <li>rätta felaktiga uppgifter (redigera din Masterprofil),</li>
            <li>radera dina uppgifter ("radera konto" i Inställningar),</li>
            <li>invända mot eller begränsa behandling, samt återkalla samtycke,</li>
            <li>få dina uppgifter i ett maskinläsbart format (dataportabilitet).</li>
          </ul>
          <p>Du har också rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY).</p>
        </Section>

        <Section title="11. Säkerhet">
          <p>Lösenord lagras krypterade (bcrypt). Vi tillämpar skydd mot upprepade inloggningsförsök, kan logga ut alla enheter vid lösenordsbyte och för en säkerhetslogg. All åtkomst till dina uppgifter kräver inloggning.</p>
        </Section>

        <Section title="12. Ändringar i denna policy">
          <p>Vi kan uppdatera policyn vid behov. Väsentliga ändringar informerar vi om i tjänsten. Datumet högst upp visar när policyn senast uppdaterades.</p>
        </Section>

        <div className="mt-10 rounded-2xl border border-border bg-white p-6 text-sm text-muted-foreground">
          Har du frågor om din integritet? Kontakta oss via <Link to="/hjalp" className="text-primary font-medium underline underline-offset-2">Hjälp &amp; feedback</Link> eller mejla <a href="mailto:ludwig_hamn@outlook.com" className="text-primary font-medium underline underline-offset-2">ludwig_hamn@outlook.com</a>.
        </div>
      </main>
    </div>
  );
}
